import { CheckCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import type { PropsWithChildren } from "react";
import { useState } from "react";

import Button from "~/components/buttons/Button/Button.tsx";
import { ComboBoxPlain } from "~/components/formElements/ComboBox.tsx";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Headline from "~/components/Headline/Headline.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import { useCompanySelectOptions } from "~/modules/client/hooks/useCompanySelectOptions.ts";
import type { QuotaType } from "~/modules/quota/api/quota/quotaTypes.ts";


type QuotaTypeOptionProps = PropsWithChildren & {
	type: QuotaType;
	isSelected: boolean;
	onClick: (type: QuotaType) => void;
}

const QuotaTypeOption = ({ children, isSelected, onClick, type }: QuotaTypeOptionProps) => {
	return <button type="button"
				   onClick={() => onClick(type)}
				   className="group grid w-full grid-cols-[auto_1fr] rounded-lg text-sm hover:bg-gray-100"
	>
		<div className={clsx("flex w-10 flex-col items-center justify-center rounded-l-lg border",
			isSelected ? "border-success-600 bg-success-600" : "border-gray-300")}>
			{isSelected ?
				<CheckCircleIcon className="size-6 text-white" /> :
				<div className="size-5 rounded-full border border-gray-300" />}
		</div>
		<div className="flex flex-col items-start justify-start rounded-r-lg border border-l-0 border-gray-300 px-4 py-3">
			<Headline type="h5">{type === "advance" ? "Neue Rechnung" : "Existierende Rechnungen"}</Headline>
			<ul className="mt-1 list-disc pl-4 text-left leading-5 text-gray-500">
				{children}
			</ul>
		</div>
	</button>;
};

type Props = {
	onCancel: () => void;
	onSubmit: (companyId: string, type: QuotaType) => void;
};

export const QuotaCompanyAndTypeSelector = ({ onCancel, onSubmit }: Props) => {
	const [selectedType, setSelectedType] = useState<QuotaType | undefined>("advance");
	const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");

	const companySelectOptions = useCompanySelectOptions();

	const handleSubmit = () => {
		if (selectedCompanyId && selectedType) {
			onSubmit(selectedCompanyId, selectedType);
		}
	};

	return <div className="auto-grid-sidebar">
		<SidebarHeader>
			<SidebarHeaderHeadline>Kunde und Abrechnungsart wählen</SidebarHeaderHeadline>
		</SidebarHeader>
		<SidebarContent>
			<FormSectionGroup>
				<FormSection title="Kunde">
					<div className="space-y-2">
						<ComboBoxPlain optionsData={companySelectOptions}
									   allowNew={false}
									   placeholder="Bitte auswählen..."
									   value={selectedCompanyId}
									   onChange={(value) => typeof value === "string" ? setSelectedCompanyId(value) : undefined}
						/>
						<div className="text-sm text-gray-500">Ein Abrechnungskontingent ist immer einem Kunden
							zugewiesen.
							Es
							kann für alle Tochterfirmen des Kunden und deren Projekte verwendet werden.
						</div>
					</div>
				</FormSection>
				<FormSection title="Abrechnungsart">
					<div className="space-y-4">
						<QuotaTypeOption type="advance"
										 isSelected={selectedType === "advance"}
										 onClick={setSelectedType}>
							<li>Das Kontingent basiert auf einer Vorauszahlungsrechnung.</li>
							<li>Diese  Rechnung wird bei der Erstellung des Kontingents generiert.</li>
						</QuotaTypeOption>
						<QuotaTypeOption type="invoice-backed"
										 isSelected={selectedType === "invoice-backed"}
										 onClick={setSelectedType}>
							<li>Eine oder mehrere existierende Rechnungen des ausgewählten Kunden enthalten Überschüsse, die für
								zukünftige
								Erfassungen genutzt werden sollen.
							</li>
							<li>Hinweis: Diese Abrechnungsart kann auch gewählt werden, falls die Rechnungen noch
								nicht
								gestellt wurden. Das Kontingent ist allerdings erst nutzbar, sobald die Rechnungen
								zugewiesen
								wurden.
							</li>
						</QuotaTypeOption>
					</div>
					<div className="mt-1 text-sm text-gray-500">Achtung: Die Abrechnungsart kann nach der Erstellung des
						Kontingents nicht mehr geändert werden.
					</div>
				</FormSection>
			</FormSectionGroup>
		</SidebarContent>
		<SidebarFooter>
			<Button
				onClick={handleSubmit}
				disabled={!selectedCompanyId || !selectedType}
			>
				Weiter
			</Button>
			<Button theme="white"
					onClick={onCancel}>
				Abbrechen
			</Button>
		</SidebarFooter>
	</div>;
};
